import React, { useEffect, useState } from "react";
import { Plus, Route, User } from "lucide-react";
import { cn } from "../../lib/utils";
import useRetryFetch from "../../hooks/useRetryFetch";
import { MmObjectType, MmPath, MmRegister, listPaths } from "../../api/mentalModels";
import { useMmText } from "../../hooks/useMmText";

/** Fallback copy for the built-in scripts (design §6.4). */
const SCRIPT_FALLBACKS: Record<string, { name: string; desc: string }> = {
  full_design: {
    name: "The Design Method",
    desc: "From what's known to a hardened design committed to reality — the full arc.",
  },
  belief_audit: {
    name: "Belief Audit",
    desc: "Trace where a belief came from, test it against reality, keep or retire it.",
  },
  fear_shrinker: {
    name: "Fear Shrinker",
    desc: "Step back, split the dread into parts, and approach the smallest one.",
  },
  decision_detox: {
    name: "Decision Detox",
    desc: "Bound the question, weigh the options, and run the cheapest real test.",
  },
  identity_update: {
    name: "Identity Update",
    desc: "Retire an outgrown self-image and anchor its successor.",
  },
};

interface Props {
  objectType?: MmObjectType;
  register: MmRegister;
  onPick: (path: MmPath) => void;
  onCreateNew?: () => void;
  compact?: boolean;
}

const MmPathPicker: React.FC<Props> = ({ objectType, register, onPick, onCreateNew, compact }) => {
  const retryFetch = useRetryFetch();
  const { tm } = useMmText(register);
  const [paths, setPaths] = useState<MmPath[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    listPaths(retryFetch)
      .then((res) => {
        const all = [...res.scripts, ...res.templates];
        // Type-matching paths first; generic ones after
        const matches = (p: MmPath) =>
          !objectType || !p.objectTypes.length || p.objectTypes.includes(objectType);
        setPaths([...all.filter(matches), ...all.filter((p) => !matches(p))]);
      })
      .catch(() => setPaths([]))
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loaded || (!paths.length && !onCreateNew)) return null;

  return (
    <div className={cn("space-y-1.5", compact && "max-h-64 overflow-y-auto pe-1")}>
      {paths.map((p) => {
        const fb = p.kind === "script" ? SCRIPT_FALLBACKS[p.id] : null;
        const name = p.kind === "script" ? tm(`script_${p.id}_name`, fb?.name || p.id) : p.name || "";
        const desc =
          p.kind === "script" ? tm(`script_${p.id}_desc`, fb?.desc || "") : p.description || "";
        const typeMatch =
          !objectType || !p.objectTypes.length || p.objectTypes.includes(objectType);
        return (
          <button
            key={`${p.kind}-${p.id}`}
            onClick={() => onPick(p)}
            className={cn(
              "w-full text-start rounded-xl border p-3 min-h-[44px] cursor-pointer bg-white",
              "transition-all duration-150 hover:border-primary/30 hover:shadow-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              typeMatch ? "border-border/80" : "border-border/50 opacity-60"
            )}
          >
            <span className="flex items-center gap-1.5 text-sm font-medium">
              {p.kind === "template" ? (
                <User className="w-3.5 h-3.5 text-violet-500" />
              ) : (
                <Route className="w-3.5 h-3.5 text-primary/70" />
              )}
              {name}
              {p.isMine && (
                <span className="text-[10px] uppercase rounded bg-violet-100 text-violet-700 px-1.5 py-0.5">
                  {tm("path_mine", "yours")}
                </span>
              )}
            </span>
            {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
            <p className="text-[11px] text-muted-foreground/80 mt-1">
              {p.steps.map((s) => tm(`op_${s}_title`, s)).join(" → ")}
            </p>
          </button>
        );
      })}
      {onCreateNew && (
        <button
          onClick={onCreateNew}
          className={cn(
            "w-full flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-border p-2.5 min-h-[40px]",
            "text-xs text-muted-foreground cursor-pointer transition-all duration-150",
            "hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          )}
        >
          <Plus className="w-3.5 h-3.5" />
          {tm("create_own_path", "Create your own path")}
        </button>
      )}
    </div>
  );
};

export default MmPathPicker;
